import {
  Button,
  Dialog,
  DialogContent,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../core/modules/global';
import { CREATE_LOCATION_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useCompanyCore } from '../../../../core/modules/company';
import { PostLocationPayloadRequest } from '../../../../core/modules/location/interfaces/post-location';
import { useLocationCore } from '../../../../core/modules/location';
import { Warehouse } from '../../../../core/interfaces/warehouse.interface';
import { useWarehouseCore } from '../../../../core/modules/warehouse';
import { useMasterCore } from '../../../../core/modules/master';
import { Type } from '../../../../core/interfaces/type.interface';

const schema = yup.object().shape({
  code: yup.string().required('Campo requerido'),
  description: yup.string().nullable(),
  level: yup.string().required('Campo requerido'),
  aisle: yup.string().required('Campo requerido'),
  column: yup.string().required('Campo requerido'),
  location_type_id: yup.number().required('Campo requerido'),
  warehouse_id: yup.number().required('Campo requerido'),
  channel_type_ids: yup.array().of(yup.number().required()).min(1, 'Debe seleccionar al menos un canal').required('Campo obligatorio'),
});

const CreateDialog = () => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { postLocation, getLocations } = useLocationCore();
  const { warehouses } = useWarehouseCore();
  const { locationTypes, channelTypes } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);

  const { control, formState, handleSubmit, resetField, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  // Obtener los canales válidos de la bodega seleccionada
  const validChannelTypes = selectedWarehouse 
    ? channelTypes.filter(channel => 
        selectedWarehouse.channel_type_ids && 
        selectedWarehouse.channel_type_ids.includes(channel.id)
      )
    : [];

  const resetForm = useCallback(() => {
    resetField('code');
    resetField('description');
    resetField('level');
    resetField('aisle');
    resetField('column');
    resetField('location_type_id');
    resetField('channel_type_ids');
    resetField('warehouse_id');
    setSelectedWarehouse(null);
    closeDialog(CREATE_LOCATION_DIALOG);
  }, [closeDialog, resetField, setValue]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId) {
        // Ensure channel_type_ids is a valid number array
        const channelIds = Array.isArray(values.channel_type_ids)
          ? values.channel_type_ids.filter((id: any) => id !== undefined && id !== null)
          : [];

        const payload: PostLocationPayloadRequest = {
          ...values,
          channel_type_ids: channelIds
        };

        setLoading(true);
        postLocation(companyId, payload, () => {
          resetForm();
          getLocations(companyId);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, postLocation, resetForm, getLocations]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(CREATE_LOCATION_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Crear Ubicación
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="warehouse_id"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth required>
                          <InputLabel id="warehouse-label">
                            Bodega
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="warehouse-label"
                            disabled={loading}
                            label="Bodega"
                            fullWidth
                            required
                            onChange={(e) => {
                              field.onChange(e);
                              const warehouse = warehouses.find(w => w.id === e.target.value);
                              setSelectedWarehouse(warehouse || null);
                              
                              // Limpiar canales seleccionados cuando cambia la bodega
                              resetField('channel_type_ids');
                              
                              // También limpiar el valor en el formulario para asegurar consistencia
                              setValue('channel_type_ids', []);
                            }}
                          >
                            {warehouses.map((store: Warehouse) => (
                              <MenuItem key={store.id} value={store.id}>{store.code}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="code"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Código"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.code}
                          helperText={errors?.code?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="aisle"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Pasillo"
                          type="number"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.aisle}
                          helperText={errors?.aisle?.message}
                          required
                          fullWidth
                          placeholder="Ej: 1,2,3"
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="level"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nivel"
                          type="number"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.level}
                          helperText={errors?.level?.message}
                          required
                          fullWidth
                          placeholder="Ej: 1,2,3"
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="column"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Columna"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.column}
                          helperText={errors?.column?.message}
                          required
                          fullWidth
                          placeholder="Ej: Columna 1, Estante 3"
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="location_type_id"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth required>
                          <InputLabel id="location-type-label">
                            Tipo
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="location-type-label"
                            disabled={loading}
                            label="Tipo"
                            fullWidth
                            required
                          >
                            {locationTypes.map((location: Type) => (
                              <MenuItem key={location.id} value={location.id}>{location.description}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="channel_type_ids"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth>
                          <InputLabel id="channel-label" required>
                            Canales
                          </InputLabel>
                          <Select
                            {...field}
                            value={Array.isArray(field.value) ? field.value : []}
                            labelId="channel-label"
                            label="Canales"
                            disabled={loading || !selectedWarehouse}
                            required
                            multiple
                            input={<OutlinedInput label="Canales" />}
                            renderValue={(selected) => {
                              const selectedIds = selected as number[];
                              return selectedIds
                                .map((id) => validChannelTypes.find((type) => type.id === id)?.name)
                                .filter(Boolean)
                                .join(', ');
                            }}
                          >
                            {validChannelTypes.map((type) => (
                              <MenuItem key={type.id} value={type.id}>
                                <Checkbox checked={field.value?.includes(type.id)} />
                                <ListItemText primary={type.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description}
                          helperText={errors?.description?.message}
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="flex justify-center">
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialog;
